import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { paths } from 'src/routes/paths';
import { useCollectiblesStore } from 'src/store/collectibles/collectibles.store';
import * as Yup from 'yup';

const STATUS_OPTIONS = ['active', 'inactive'];
const STATUS_LABEL = ['Ativo', 'Inativo'];

export default function useCollectiblesDetails() {
  const carouselRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const { pathname } = useLocation();
  const isEdit = pathname.includes('edit');
  const [searchParams] = useSearchParams();
  const collectionId = searchParams.get('collectionId');

  const {
    isLoadingCollectibleDetails,
    collectibleDetails,
    locations,
    isLoadingLocations,
    getCollectibleDetails,
    createCollectible,
    updateCollectible,
    resetCollectibleDetails,
    getLocations,
  } = useCollectiblesStore();

  const [renderedLocations, setRenderedLocations] = useState<any[]>([]);

  useEffect(() => {
    getLocations();
    if (id) {
      getCollectibleDetails(id);
    } else {
      resetCollectibleDetails();
    }
  }, []);

  useEffect(() => {
    setRenderedLocations(locations);
  }, [locations]);

  const defaultValues = useMemo(
    () => ({
      type: collectibleDetails?.type || 'free',
      name: collectibleDetails?.name || '',
      simplifiedName: collectibleDetails?.simplifiedName || '',
      description: collectibleDetails?.description || '',
      imagePreview: collectibleDetails?.imagePreview || '',
      additionalImages: collectibleDetails?.additionalImages || [],
      icon: collectibleDetails?.icon || '',
      locations: collectibleDetails?.entity
        ? { value: collectibleDetails.entity.entityId, label: collectibleDetails.entity.entityName }
        : '',
      collectionId: collectionId || collectibleDetails?.collectionId || '',
      daysToRescue: collectibleDetails?.daysToRescue || 0,
      price: collectibleDetails?.price || 0,
      totalQuantity: collectibleDetails?.totalQuantity || 0,
      priority: collectibleDetails?.priority || 0,
      status: collectibleDetails?.status || 'active',
      hasCustomerVoice: !!(
        collectibleDetails?.customerVoiceData?.baseUrl ||
        collectibleDetails?.customerVoiceData?.assetsUrl ||
        collectibleDetails?.customerVoiceData?.customerVoiceFormId
      ),
      customerVoiceData: {
        baseUrl: collectibleDetails?.customerVoiceData?.baseUrl || '',
        assetsUrl: collectibleDetails?.customerVoiceData?.assetsUrl || '',
        customerVoiceFormId: collectibleDetails?.customerVoiceData?.customerVoiceFormId || '',
      },
    }),
    [collectibleDetails]
  );

  const collectibleSchema = Yup.object().shape({
    name: Yup.string().required('Campo obrigatório!'),
    simplifiedName: Yup.string().required('Campo obrigatório!'),
    description: Yup.string().required('Campo obrigatório!'),
    imagePreview: Yup.mixed().required('Campo obrigatório!'),
    additionalImages: Yup.array().of(Yup.mixed()).min(1, 'Deve ter pelo menos uma imagem adicional'),
    icon: Yup.mixed().required('Campo obrigatório!'),
    locations: Yup.mixed().required('Campo obrigatório!'),
    collectionId: Yup.string(),
    daysToRescue: Yup.number().min(1, 'Deve ser maior ou igual a 0').required('Campo obrigatório!'),
    price: Yup.number().min(0, 'Deve ser maior ou igual a 0').required('Campo obrigatório!'),
    totalQuantity: Yup.number().min(1, 'Deve ser maior que 0').required('Campo obrigatório!'),
    priority: Yup.number().min(0, 'Deve ser maior ou igual a 0').required('Campo obrigatório!'),
    status: Yup.string().required('Campo obrigatório!'),
    hasCustomerVoice: Yup.boolean().required(),
    customerVoiceData: Yup.object({
      baseUrl: Yup.string(),
      assetsUrl: Yup.string(),
      customerVoiceFormId: Yup.string(),
    })
      .when('hasCustomerVoice', {
        is: (hasCustomerVoice: boolean) => hasCustomerVoice,
        then: (customerVoiceSchema) =>
          customerVoiceSchema.shape({
            baseUrl: Yup.string().required('Campo obrigatório'),
            assetsUrl: Yup.string().required('Campo obrigatório'),
            customerVoiceFormId: Yup.string().required('Campo obrigatório'),
          }),
        otherwise: (customerVoiceSchema) =>
          customerVoiceSchema.shape({
            baseUrl: Yup.string().notRequired(),
            assetsUrl: Yup.string().notRequired(),
            customerVoiceFormId: Yup.string().notRequired(),
          }),
      })
      .required(),
  });

  const methods = useForm({
    resolver: yupResolver(collectibleSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    control,
    // getValues,
    formState: { isSubmitting },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'additionalImages',
  });

  const [shouldScroll, setShouldScroll] = useState(false);

  const removeAndEnsureEmpty = (index: number) => {
    remove(index);
    setShouldScroll(true);
  };

  useEffect(() => {
    append('');
    if (shouldScroll) {
      setTimeout(() => {
        if (carouselRef.current) {
          carouselRef.current.scrollLeft = carouselRef.current.scrollWidth;
          setShouldScroll(false);
        }
      }, 100);
    }
  }, [shouldScroll]);

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);

      append('');
      setTimeout(() => {
        if (carouselRef.current) {
          carouselRef.current.scrollLeft = carouselRef.current.scrollWidth;
        }
      }, 100);
    }
  }, [collectibleDetails, defaultValues, reset]);

  const onSubmit = handleSubmit(async (data: any) => {
    const cleanedAdditionalImages = Array.isArray(data.additionalImages)
      ? data.additionalImages.filter((img: string) => img !== '')
      : [];

    const entity = {
      type: 'local',
      entityName: data.locations.label,
      entityId: data.locations.value,
    };
    const updatedData = {
      ...data,
      additionalImages: cleanedAdditionalImages,
      entity,
      locations: data.locations.value,
      collectionId: collectionId || data.collectionId,
      customerVoiceData: data.hasCustomerVoice ? data.customerVoiceData : null,
    };
    try {
      if (isEdit && id) {
        await updateCollectible(id, updatedData);
        enqueueSnackbar('Colecionável atualizado com sucesso!', { variant: 'success' });
        navigate(`${paths.dashboard.collectionsDetails(collectionId!)}`);
      } else {
        await createCollectible(collectionId!, updatedData);
        enqueueSnackbar('Colecionável criado com sucesso!', { variant: 'success' });
        navigate(`${paths.dashboard.collectionsDetails(collectionId!)}`);
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  });

  const handleDropImagePreview = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();

        img.src = reader.result as string;

        img.onload = () => {
          setValue(
            'imagePreview',
            Object.assign(file, {
              preview: reader.result,
            })
          );
        };
      };

      reader.readAsDataURL(file);
    },
    [setValue]
  );

  const handleDropAdditionalImages = useCallback(
    (acceptedFiles: File[], index: number) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();
        img.src = reader.result as string;

        img.onload = () => {
          setValue(`additionalImages.${index}`, Object.assign(file, { preview: reader.result }));

          if (index === fields.length - 1) {
            append('');
            setTimeout(() => {
              if (carouselRef.current) {
                carouselRef.current.scrollLeft = carouselRef.current.scrollWidth;
              }
            }, 100);
          }
        };
      };

      reader.readAsDataURL(file);
    },
    [setValue, enqueueSnackbar, fields, append]
  );
  const handleDropIconImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      const reader = new FileReader();

      reader.onload = () => {
        const img = new Image();

        img.src = reader.result as string;

        img.onload = () => {
          setValue(
            'icon',
            Object.assign(file, {
              preview: reader.result,
            })
          );
        };
      };

      reader.readAsDataURL(file);
    },
    [setValue]
  );

  return {
    isEdit,
    id,
    isLoadingCollectibleDetails,
    isSubmitting,
    methods,
    fields,
    isLoadingLocations,
    renderedLocations,
    STATUS_LABEL,
    STATUS_OPTIONS,
    collectionId,
    carouselRef,
    append,
    remove,
    navigate,
    enqueueSnackbar,
    onSubmit,
    handleDropImagePreview,
    handleDropAdditionalImages,
    handleDropIconImage,
    removeAndEnsureEmpty,
    collectibleDetails,
  };
}
